var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import moment from 'moment';
import { useCallback, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { getAllDayTimeRange } from '../utils/allDayDateUtils';
import { enforceTimeConstraints, isStartAndEndTimeValid as checkTimeValidity, } from '../utils/validationUtils';
var normalizeTimeToDate = function (_a) {
    var selectedDate = _a.selectedDate, time = _a.time, timezone = _a.timezone;
    return time
        .clone()
        .set({
        date: selectedDate.date(),
        month: selectedDate.month(),
        year: selectedDate.year(),
    })
        .tz(timezone);
};
export function useEventDateTime(_a) {
    var timezone = _a.timezone, _b = _a.disallowPastStart, disallowPastStart = _b === void 0 ? false : _b;
    var setValue = useFormContext().setValue;
    var getValues = useFormContext().getValues;
    // Store the original time values before setting all-day
    var originalTimeRef = useRef({
        start: getValues('startTime'),
        end: getValues('endTime'),
    });
    // Watched (not getValues) so validity re-derives as the user edits;
    // getValues is referentially stable, so a memo keyed on it computes only
    // once at mount and the inline error freezes.
    var _c = __read(useWatch({ name: ['startTime', 'endTime'] }), 2), startTime = _c[0], endTime = _c[1];
    var isStartAndEndTimeValid = useMemo(function () { return checkTimeValidity(startTime, endTime, moment(), disallowPastStart); }, [startTime, endTime, disallowPastStart]);
    var handleTimeRange = useCallback(function (format, time) {
        var start = null;
        var end = null;
        if (time.start) {
            start = normalizeTimeToDate({
                selectedDate: getValues('date'),
                time: moment.tz(time.start, format, timezone),
                timezone: timezone,
            });
            setValue('startTime', start, {
                shouldDirty: true,
            });
        }
        if (time.end) {
            end = normalizeTimeToDate({
                selectedDate: getValues('date'),
                time: moment.tz(time.end, format, timezone),
                timezone: timezone,
            });
            setValue('endTime', end, {
                shouldDirty: true,
            });
        }
    }, [timezone, setValue, getValues]);
    var handleTimeChange = useCallback(function (format, time, type, normalizeTo) {
        if (!time)
            return;
        var normalizedTime = normalizeTimeToDate({
            selectedDate: normalizeTo,
            time: moment.tz(time, format, timezone),
            timezone: timezone,
        });
        setValue(type, normalizedTime, { shouldDirty: true });
        // Read both fields after the setValue (store update is synchronous) and
        // enforce ordering + minimum gap. The just-edited field wins; the other
        // is adjusted if needed.
        var currentStart = getValues('startTime');
        var currentEnd = getValues('endTime');
        var _a = enforceTimeConstraints({
            changedField: type,
            startTime: currentStart,
            endTime: currentEnd,
        }), constrainedStart = _a.startTime, constrainedEnd = _a.endTime;
        if (!constrainedStart.isSame(currentStart)) {
            setValue('startTime', constrainedStart, { shouldDirty: true });
        }
        if (!constrainedEnd.isSame(currentEnd)) {
            setValue('endTime', constrainedEnd, { shouldDirty: true });
        }
    }, [timezone, setValue, getValues]);
    var handleDateChange = useCallback(function (date) {
        var _a = getValues(), startTime = _a.startTime, endTime = _a.endTime;
        var isAllDay = getValues('isAllDayEvent');
        var allDayTimeRange = getAllDayTimeRange({
            selectedDate: date,
            timezone: timezone,
        });
        if (isAllDay) {
            setValue('date', date, {
                shouldDirty: true,
            });
            setValue('startTime', allDayTimeRange.startTime, {
                shouldDirty: true,
            });
            setValue('endTime', allDayTimeRange.endTime, {
                shouldDirty: true,
            });
        }
        else {
            setValue('date', date, {
                shouldDirty: true,
            });
            var _b = {
                start: normalizeTimeToDate({
                    selectedDate: date,
                    time: startTime,
                    timezone: timezone,
                }),
                end: normalizeTimeToDate({
                    selectedDate: date,
                    time: endTime,
                    timezone: timezone,
                }),
            }, start = _b.start, end = _b.end;
            setValue('startTime', start, {
                shouldDirty: true,
            });
            setValue('endTime', end, {
                shouldDirty: true,
            });
        }
    }, [setValue, timezone, getValues]);
    var handleAllDayChange = useCallback(function (isAllDay) {
        var _a = getValues(), date = _a.date, startTime = _a.startTime, endTime = _a.endTime;
        var currentIsAllDay = getValues('isAllDayEvent');
        // Only proceed if the state is actually changing
        if (isAllDay === currentIsAllDay)
            return;
        if (isAllDay) {
            // Store current times before setting all-day
            originalTimeRef.current = {
                start: startTime.clone(),
                end: endTime.clone(),
            };
            var allDayTimes = getAllDayTimeRange({
                selectedDate: date,
                timezone: timezone,
            });
            setValue('startTime', allDayTimes.startTime, {
                shouldDirty: true,
            });
            setValue('endTime', allDayTimes.endTime, {
                shouldDirty: true,
            });
            setValue('isAllDayEvent', isAllDay, {
                shouldDirty: true,
            });
        }
        else {
            // Restore original times or use defaults
            var originalTimes = originalTimeRef.current;
            var _b = {
                start: normalizeTimeToDate({
                    selectedDate: date,
                    time: originalTimes.start,
                    timezone: timezone,
                }),
                end: normalizeTimeToDate({
                    selectedDate: date,
                    time: originalTimes.end,
                    timezone: timezone,
                }),
            }, start = _b.start, end = _b.end;
            setValue('startTime', start, {
                shouldDirty: true,
            });
            setValue('endTime', end, {
                shouldDirty: true,
            });
            setValue('isAllDayEvent', isAllDay, {
                shouldDirty: true,
            });
        }
    }, [setValue, timezone, getValues]);
    // Applies a full date + start/end range at once (e.g. a suggested meeting
    // time). The day is taken from `start`; both times are normalized to the
    // event timezone.
    var applyDateTimeRange = useCallback(function (start, end) {
        var normalizedStart = start.clone().tz(timezone);
        var normalizedEnd = end.clone().tz(timezone);
        setValue('date', normalizedStart.clone().startOf('day'), {
            shouldDirty: true,
        });
        setValue('startTime', normalizedStart, { shouldDirty: true });
        setValue('endTime', normalizedEnd, { shouldDirty: true });
    }, [setValue, timezone]);
    return {
        handleDateChange: handleDateChange,
        handleAllDayChange: handleAllDayChange,
        handleTimeRange: handleTimeRange,
        handleTimeChange: handleTimeChange,
        applyDateTimeRange: applyDateTimeRange,
        isStartAndEndTimeValid: isStartAndEndTimeValid,
    };
}
